// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const SERIES_COLOR = '#53A796';

const margins = {
  top: 20,
  right: 90,
  bottom: 25,
  left: 30,
};

const parser = d3.timeParse('%Y-%m-%d');

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let lastUpdatedStr;
let latestMonth;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%Y-%m-%d');

const recessionData = [
  {
    start: '2020-03-01', end: '2020-04-01', label: 'Covid-19', labelYOffset: -13, labelXOffset: 5,
  },
  {
    start: '2020-03-01', end: '2020-04-01', label: 'recession', labelYOffset: 0, labelXOffset: 5,
  },
];

let dekText;

d3.json('https://ix.cnn.io/data/jobs-indicators/total_job_openings.json').then((data) => {
  // Map and filter data, removing any invalid entries
  chartData = data.data
    .map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      value: +d.value,
    }))
    .filter(d => d.date && !isNaN(d.value) && d.value !== null);

  // Validate we have data to display
  if (chartData.length === 0) {
    console.error('No valid job openings data available');
    d3.select('#graphic').append('p')
      .style('text-align', 'center')
      .style('padding', '40px')
      .text('Data temporarily unavailable');
    return;
  }

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.value);

  // Get the last data point
  const lastDataArray = chartData.filter(d => d.date.getTime() === maxDomain.getTime());
  lastData = lastDataArray.length > 0 ? lastDataArray[0] : chartData[chartData.length - 1];

  lastUpdatedStr = data.lastUpdatedStr;
  latestMonth = d3.timeFormat('%B %Y')(lastData.date);

  dekText = data.summaryHtml;

  if (dekText && dekText !== '') {
    d3.select('h2.dek')
      .html(dekText);
  }

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 2 : 5;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%Y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => {
      const millions = d / 1000000;
      return i == x.length - 1 ? `${d3.format('.0f')(millions)}M` : `${d3.format('.0f')(millions)}`;
    });

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const area = d3.area()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y0(yScale(0))
    .y1(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  recessionData.forEach((rec) => {
    const startDate = new Date(rec.start);
    const endDate = new Date(rec.end);

    g.append('rect')
      .attr('x', xScale(startDate))
      .attr('width', xScale(endDate) - xScale(startDate))
      .attr('y', 0)
      .attr('height', chartHeight)
      .attr('fill', '#e3e3e3')
      .attr('opacity', 0.5);
  });

  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(0))
    .attr('y2', yScale(0))
    .attr('stroke', '#393939')
    .attr('stroke-width', 1);

  g.append('path')
    .datum(chartData)
    .attr('class', 'area')
    .attr('fill', SERIES_COLOR)
    .attr('d', area);

  const line = g.append('g')
    .attr('class', 'line');

  const lineWidth = window.innerWidth < 768 ? 1.5 : 2;

  line.append('path')
    .datum(chartData)
    .attr('stroke', SERIES_COLOR)
    .attr('stroke-width', lineWidth)
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  recessionData.forEach((rec) => {
    const endDate = new Date(rec.end);
    const labelXOffset = rec.labelXOffset || 0;
    const labelYOffset = rec.labelYOffset || 0;

    g.append('text')
      .attr('x', xScale(endDate) + labelXOffset)
      .attr('y', chartHeight - 10 + labelYOffset)
      .attr('text-anchor', 'begin')
      .attr('class', 'label-recession')
      .text(rec.label);
  });

  if (lastData && lastData.date && !isNaN(lastData.value)) {
    const endpoint = g.append('g')
      .attr('class', 'endpoint')
      .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

    endpoint.append('circle')
      .attr('class', 'pulse')
      .attr('r', 4)
      .attr('fill', SERIES_COLOR);

    endpoint.append('circle')
      .attr('class', 'end-dot')
      .attr('r', 4)
      .attr('fill', SERIES_COLOR)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    endpoint.append('text')
      .attr('class', 'endpoint-date')
      .attr('x', 9)
      .attr('dy', '-0.25em')
      .text(d3.timeFormat('%b %Y')(lastData.date));

    endpoint.append('text')
      .attr('class', 'endpoint-value')
      .attr('x', 9)
      .attr('dy', '0.95em')
      .text(`${d3.format(',.2f')(lastData.value / 1000000)}M`);
  }

  const tooltip = container.append('div')
    .attr('class', 'tooltip')
    .style('display', 'none');

  const hoverDot = g.append('circle')
    .attr('class', 'hover-dot')
    .attr('r', 4)
    .attr('fill', SERIES_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5)
    .style('display', 'none');

  const hoverTargets = chartData.map((d, i) => {
    const left = i === 0
      ? 0
      : (xScale(chartData[i - 1].date) + xScale(d.date)) / 2;
    const right = i === chartData.length - 1
      ? chartWidth
      : (xScale(d.date) + xScale(chartData[i + 1].date)) / 2;
    return { ...d, left, right };
  });

  g.append('g')
    .attr('class', 'hover-targets')
    .selectAll('rect')
    .data(hoverTargets)
    .enter()
    .append('rect')
    .attr('x', d => d.left)
    .attr('width', d => d.right - d.left)
    .attr('y', 0)
    .attr('height', chartHeight)
    .attr('fill', 'transparent')
    .on('mouseover', () => {
      tooltip.style('display', 'block');
      hoverDot.style('display', 'block');
    })
    .on('mousemove', (d) => {
      const [mx, my] = d3.mouse(container.node());

      hoverDot
        .attr('cx', xScale(d.date))
        .attr('cy', yScale(d.value));

      tooltip.html(`<strong>${d3.timeFormat('%b %Y')(d.date)}</strong><br>${d3.format(',.2f')(d.value / 1000000)}M`);

      const tooltipWidth = tooltip.node().offsetWidth;
      const left = (mx + 12 + tooltipWidth > initialWidth)
        ? mx - tooltipWidth - 12
        : mx + 12;

      tooltip
        .style('left', `${left}px`)
        .style('top', `${my - 38}px`);
    })
    .on('mouseout', () => {
      tooltip.style('display', 'none');
      hoverDot.style('display', 'none');
    });

  // Append the current date as a timestamp below the chart
  const currentDateString = `Notes: Data values are monthly and represent total nonfarm job openings. Last updated ${lastUpdatedStr}.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
